import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Globe, Layout, Zap, ShieldCheck, 
  ArrowRight, Sparkles, Server, Cpu, Rocket, Calendar,
  Code2, ShoppingBag, FileText, Smartphone
} from "lucide-react";
import { 
  SiReact, SiNextdotjs, SiTypescript, SiTailwindcss, 
  SiNodedotjs, SiWordpress, SiShopify, SiPhp, SiPostgresql 
} from "react-icons/si";
import { FaAws } from "react-icons/fa6";

import WebServices from "../../plans/webservices";

export default function WebsiteDevelopmentService() {
  const webDevTypes = [
    {
      title: "Custom Web Application Development",
      icon: <Code2 className="text-blue-600 dark:text-cyan-400" size={28} />,
      overview: "Tailor-made, high-performance web applications built from scratch to handle complex business logic, user dashboards, and unique workflows.",
      coreLabel: "Core Architectures",
      techs: ["React 19", "Next.js", "Node.js", "Express", "PHP/Laravel", "SQL/NoSQL Databases"]
    },
    {
      title: "E-Commerce & Retail Store Development",
      icon: <ShoppingBag className="text-emerald-600 dark:text-emerald-400" size={28} />,
      overview: "Scalable online storefronts designed for maximum conversion rates, complete with secure payment gateway integrations (Razorpay, Paytm, Stripe), multi-currency support, and inventory management.",
      coreLabel: "Core Platforms",
      techs: ["Shopify", "Shopify Plus", "WooCommerce", "Razorpay / Stripe", "Headless E-Commerce"]
    },
    {
      title: "Content Management Systems (CMS) & Enterprise Portals",
      icon: <FileText className="text-indigo-600 dark:text-violet-400" size={28} />,
      overview: "Intuitive, secure, and easily maintainable platforms that empower internal teams to manage content, publish updates, and scale media outreach effortlessly.",
      coreLabel: "Core Technologies",
      techs: ["WordPress (Custom Themes & Plugins)", "Headless CMS Solutions", "Enterprise PHP Frameworks"]
    },
    {
      title: "Progressive Web Apps (PWAs) & Mobile-First Solutions",
      icon: <Smartphone className="text-rose-600 dark:text-rose-400" size={28} />,
      overview: "Lightning-fast web experiences that mimic native mobile applications, offering offline functionality, push notifications, and ultra-responsive layouts across all devices.",
      coreLabel: "Core Tech",
      techs: ["React PWA", "Next.js", "Tailwind CSS", "Service Workers"]
    }
  ];

  const capabilities = [
    {
      icon: <Layout className="text-blue-600 dark:text-cyan-400" size={26} />,
      title: "Custom React & Next.js Portals",
      desc: "Single-page and server-side rendered web applications built for blazing speed, SEO dominance, and sub-second load times.",
      highlights: ["React 19 & Next.js", "SSR & Hydration", "Sub-second Loading"]
    },
    {
      icon: <Globe className="text-indigo-600 dark:text-violet-400" size={26} />,
      title: "WordPress & CMS Architecture",
      desc: "Turnkey custom WordPress and CMS setups with tailored theme engineering, page builder optimization, and plugin security.",
      highlights: ["Custom Themes", "WooCommerce", "Security Hardening"]
    },
    {
      icon: <Server className="text-cyan-600 dark:text-cyan-300" size={26} />,
      title: "Cloud Infrastructure & SSL",
      desc: "Automated SSL certificates, Cloudflare CDN integration, DDoS mitigation, and high-availability web hosting deployment.",
      highlights: ["AWS / Cloudflare", "Free SSL Setup", "DDoS Mitigation"]
    },
    {
      icon: <Zap className="text-amber-500 dark:text-amber-400" size={26} />,
      title: "Core Web Vitals & Speed Optimization",
      desc: "Achieving 95+ Google Lighthouse scores with lazy loading, asset compression, and clean code optimization.",
      highlights: ["95+ Lighthouse", "Asset Compression", "Lazy Loading"]
    },
    {
      icon: <ShieldCheck className="text-emerald-600 dark:text-emerald-400" size={26} />,
      title: "Technical SEO & Schema Markup",
      desc: "Structured JSON-LD schema, canonical tagging, XML sitemaps, and mobile responsiveness for search engine visibility.",
      highlights: ["JSON-LD Schema", "XML Sitemaps", "Mobile Responsive"]
    },
    {
      icon: <Cpu className="text-rose-600 dark:text-rose-400" size={26} />,
      title: "API & Third-Party Integrations",
      desc: "Seamless integration with CRM tools, payment portals, webhooks, analytics suites, and custom database backends.",
      highlights: ["REST & GraphQL", "Payment Gateways", "CRM Webhooks"]
    }
  ];

  const techStackData = [
    {
      name: "React 19",
      category: "Frontend Engine",
      icon: React.createElement(SiReact as any, { className: "text-[#61DAFB]", size: 32 })
    },
    {
      name: "Next.js",
      category: "SSR & Full Stack",
      icon: React.createElement(SiNextdotjs as any, { className: "text-slate-900 dark:text-white", size: 32 })
    },
    {
      name: "TypeScript",
      category: "Typed JavaScript",
      icon: React.createElement(SiTypescript as any, { className: "text-[#3178C6]", size: 32 })
    },
    {
      name: "Tailwind CSS",
      category: "Utility Styling",
      icon: React.createElement(SiTailwindcss as any, { className: "text-[#06B6D4]", size: 32 })
    },
    {
      name: "Node.js",
      category: "Backend Runtime",
      icon: React.createElement(SiNodedotjs as any, { className: "text-[#5FA04E]", size: 32 })
    },
    {
      name: "WordPress",
      category: "CMS Solution",
      icon: React.createElement(SiWordpress as any, { className: "text-[#21759B]", size: 32 })
    },
    {
      name: "Shopify",
      category: "E-Commerce",
      icon: React.createElement(SiShopify as any, { className: "text-[#96BF48]", size: 32 })
    },
    {
      name: "PHP",
      category: "Server Scripting",
      icon: React.createElement(SiPhp as any, { className: "text-[#777BB4]", size: 32 })
    },
    {
      name: "PostgreSQL",
      category: "Relational Database",
      icon: React.createElement(SiPostgresql as any, { className: "text-[#4169E1]", size: 32 })
    },
    {
      name: "AWS",
      category: "Cloud Infrastructure",
      icon: React.createElement(FaAws as any, { className: "text-[#FF9900]", size: 32 })
    }
  ];

  const workflow = [
    { step: "01", title: "Technical Discovery", desc: "We analyze your audience, feature requirements, branding assets, and performance goals." },
    { step: "02", title: "UI/UX & Architecture", desc: "Crafting wireframes, responsive UI designs, and high-performance frontend architecture." },
    { step: "03", title: "Development & Testing", desc: "Writing clean, scalable code with cross-browser testing and mobile optimization." },
    { step: "04", title: "Deployment & Support", desc: "Deploying to production cloud servers with 24/7 monitoring and technical maintenance." }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* BREADCRUMB & HERO */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Engineering Excellence
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            High-Performance <span className="text-blue-600 dark:text-cyan-400">Website Development</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            We engineer ultra-fast, responsive, and secure web applications tailored to scale your brand online. From custom React/Next.js platforms to turnkey CMS solutions.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link 
              to="/apply/website?plan=Custom%20React%20Portal" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-lg shadow-blue-600/25 transition-all cursor-pointer"
            >
              <Rocket size={18} className="!text-white" />
              <span className="!text-white font-bold">Apply for Web Plan</span>
              <ArrowRight size={16} className="!text-white" />
            </Link>

            <Link 
              to="/Meeting" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-blue-500 transition-all cursor-pointer shadow-sm"
            >
              <Calendar size={18} />
              <span>Book Technical Call</span>
            </Link>
          </div>
        </div>

        {/* TYPES OF WEB DEVELOPMENT SERVICES SECTION */}
        <div className="space-y-8">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3.5 py-1.5 rounded-full border border-blue-200 dark:border-blue-500/20">
              Custom Solutions Scope
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Types of Web Development Services
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
              We specialize in engineered web architectures tailored specifically to your business model and target audience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {webDevTypes.map((type, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-7 sm:p-8 space-y-6 shadow-xl hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between"
              >
                <div className="space-y-4">
                  {/* ANIMATED ICON BADGE */}
                  <div className="flex items-center gap-4">
                    <motion.div 
                      animate={{ scale: [1, 1.06, 1] }}
                      transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: idx * 0.3 }}
                      className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-md shrink-0 group-hover:scale-110 transition-transform"
                    >
                      {type.icon}
                    </motion.div>
                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white leading-snug group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                      {type.title}
                    </h3>
                  </div>

                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed font-medium">
                    {type.overview}
                  </p>
                </div>

                <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800/80">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
                    ⚡ {type.coreLabel}:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {type.techs.map((tech, tIdx) => (
                      <span key={tIdx} className="px-3 py-1 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-cyan-300 text-xs font-extrabold border border-blue-200/80 dark:border-cyan-500/30">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* VISUAL TECH STACK GRID WITH ICON GRAPHICS */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Technology Stack
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Technologies & Frameworks We Master
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              We leverage modern frameworks, cloud architectures, and databases to deliver cutting-edge web performance.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {techStackData.map((tech, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.04, y: -2 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 p-4 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 transition-all shadow-sm hover:shadow-md hover:border-blue-500/40 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  {tech.icon}
                </div>
                <div>
                  <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">{tech.name}</h4>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 block mt-0.5">{tech.category}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES GRID WITH ANIMATED ICON BADGES */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Core Capabilities
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Website Development Capabilities
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
              End-to-end engineering tailored for conversion, security, and scalability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-7 space-y-5 shadow-lg hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between cursor-pointer"
              >
                <div className="space-y-4">
                  {/* ANIMATED FLOATING ICON BADGE */}
                  <motion.div 
                    animate={{ y: [0, -4, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: idx * 0.2 }}
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/60 dark:to-cyan-950/60 border border-blue-200/80 dark:border-cyan-500/30 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform"
                  >
                    {cap.icon}
                  </motion.div>

                  <div className="space-y-2">
                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                      {cap.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed font-medium">
                      {cap.desc}
                    </p>
                  </div>

                  {/* HIGHLIGHT FEATURE CHIPS */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {cap.highlights.map((item, hIdx) => (
                      <span key={hIdx} className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800/80 text-[11px] font-bold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/80">
                        ⚡ {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80">
                  <Link to="/enquirynow" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:text-blue-700 dark:hover:text-cyan-300 group/link">
                    <span>Request Custom Quote</span>
                    <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* WORKFLOW METHODOLOGY */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-cyan-400">
              Our Development Lifecycle
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
              How We Build Your Website
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflow.map((item, idx) => (
              <div key={idx} className="bg-slate-50 dark:bg-slate-800/60 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/60 space-y-3">
                <span className="text-2xl font-black text-blue-600 dark:text-cyan-400">{item.step}</span>
                <h4 className="text-base font-extrabold text-slate-900 dark:text-white">{item.title}</h4>
                <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* WEBSITES PRICING SLIDES SECTION FROM PRICING PAGE */}
      <div className="w-full my-8">
        <WebServices />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-900/90 dark:to-indigo-950/90 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to Launch Your Next Website?
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto text-sm font-medium">
            Connect with our lead web architects today. We will analyze your specifications and send a formal project proposal within 4 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/apply/website?plan=Custom%20React%20Portal" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-white text-blue-700 hover:bg-slate-100 shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>Apply for Website Plan</span>
              <ArrowRight size={16} />
            </Link>
            <Link 
              to="/Pricing" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-blue-800/80 hover:bg-blue-800 text-white border border-blue-400/30 flex items-center gap-2 cursor-pointer"
            >
              <span>View All Pricing Plans</span>
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
